import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/shadcn/utils';

import { easeOut, easeOutClass } from './motion';

export interface Choice {
  value: string;
  label: string;
  detail?: string;
  icon?: LucideIcon;
}

interface ChoiceListProps {
  name: string;
  value: string;
  options: Choice[];
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  /** Rows fade in one after another on first render. */
  stagger?: boolean;
}

/**
 * Radio group rendered as hairline rows. Selection is carried by the primary
 * radio mark and label; the row fill stays neutral (muted when selected,
 * accent on hover) so colour isn't spent on a state the mark already shows.
 */
export const ChoiceList = ({
  name,
  value,
  options,
  onChange,
  disabled = false,
  required,
  stagger = false,
}: ChoiceListProps) => {
  const reduceMotion = useReducedMotion();

  return (
    <RadioGroupPrimitive.Root
      aria-label={name}
      value={value}
      disabled={disabled}
      required={required}
      onValueChange={onChange}
      className="divide-border divide-y border-y"
    >
      {options.map((option, index) => {
        const Icon = option.icon;

        return (
          <motion.div
            key={option.value}
            initial={stagger && !reduceMotion ? { opacity: 0, y: 6 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.3,
              ease: easeOut,
              delay: 0.05 + index * 0.035,
            }}
          >
            <RadioGroupPrimitive.Item
              value={option.value}
              className="group hover:bg-accent data-[state=checked]:bg-muted data-[state=checked]:hover:bg-muted focus-visible:ring-ring/50 flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left transition-colors duration-100 outline-none focus-visible:ring-[3px] active:bg-neutral-200/70 disabled:cursor-default disabled:opacity-60"
            >
              <span
                aria-hidden
                className="border-input group-data-[state=checked]:border-primary group-data-[state=checked]:bg-primary flex size-4 shrink-0 items-center justify-center rounded-full border bg-white transition-colors"
              >
                <CheckIcon
                  className={cn(
                    'size-3 scale-50 text-white opacity-0 transition-[opacity,transform] duration-150 group-data-[state=checked]:scale-100 group-data-[state=checked]:opacity-100 motion-reduce:scale-100',
                    easeOutClass,
                  )}
                />
              </span>
              {Icon && (
                <Icon
                  aria-hidden
                  className="text-muted-foreground group-data-[state=checked]:text-primary-700 size-4 shrink-0"
                />
              )}
              <span className="min-w-0 flex-1">
                <span className="group-data-[state=checked]:text-primary-700 block font-medium transition-colors duration-100">
                  {option.label}
                </span>
                {option.detail && (
                  <span className="text-muted-foreground block text-[13px]">
                    {option.detail}
                  </span>
                )}
              </span>
            </RadioGroupPrimitive.Item>
          </motion.div>
        );
      })}
    </RadioGroupPrimitive.Root>
  );
};
